import React, { useState, useEffect, useRef } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import DocumentUploader from './DocumentUploader';
import TypingIndicator from './TypingIndicator';
import { sendMessage, uploadDocument } from '../../services/chatbotApi';
import './ChatInterface.css';

const ChatInterface = ({ conversation, onUpdateConversation, onGenerateTitle }) => {
  const [messages, setMessages] = useState(conversation.messages || []);
  const [isTyping, setIsTyping] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState(conversation.documents || []);
  const [showUploader, setShowUploader] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages update
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Update parent conversation when messages/docs change
  useEffect(() => {
    onUpdateConversation({
      messages,
      documents: uploadedDocs,
      timestamp: new Date().toISOString()
    });
  }, [messages, uploadedDocs]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle sending a message
  const handleSendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);

    // Auto-generate title from first message
    if (messages.length === 0) {
      const title = onGenerateTitle(messageText);
      onUpdateConversation({ title });
    }

    // Show typing indicator
    setIsTyping(true);

    try {
      // Simulate API call (replace with actual API in Phase 2)
      const response = await sendMessage(
        messageText, 
        conversation.id,
        uploadedDocs
      );

      // Add assistant response
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.content,
        sources: response.sources || [],
        timestamp: new Date().toISOString()
      };

      setMessages([...updatedMessages, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        error: true,
        timestamp: new Date().toISOString()
      };

      setMessages([...updatedMessages, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (files) => {
    try {
      const uploadPromises = files.map(file => uploadDocument(file, conversation.id));
      const uploadedFiles = await Promise.all(uploadPromises);
      
      const newDocs = uploadedFiles.map(file => ({
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        url: file.url,
        uploadedAt: new Date().toISOString()
      }));

      setUploadedDocs([...uploadedDocs, ...newDocs]);
      setShowUploader(false);

      // Add system message about upload
      const systemMessage = {
        id: Date.now(),
        role: 'system',
        content: `Uploaded ${newDocs.length} document(s): ${newDocs.map(d => d.name).join(', ')}`,
        timestamp: new Date().toISOString()
      };

      setMessages([...messages, systemMessage]);
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('Failed to upload documents. Please try again.');
    }
  };

  // Remove uploaded document
  const handleRemoveDocument = (docId) => {
    setUploadedDocs(uploadedDocs.filter(doc => doc.id !== docId));
  };

  // Clear conversation
  const handleClearChat = () => {
    if (window.confirm('Are you sure you want to clear this conversation?')) {
      setMessages([]);
      onUpdateConversation({ messages: [] });
    }
  };

  return (
    <div className="chat-interface">
      {/* Chat Header */}
      <div className="chat-header">
        <div className="chat-info">
          <h2>{conversation.title}</h2>
          <span className="chat-meta">
            {messages.length} messages • {uploadedDocs.length} documents
          </span>
        </div>
        <div className="chat-actions">
          <button 
            className="btn-icon"
            onClick={() => setShowUploader(!showUploader)}
            title="Upload Documents"
          >
            📎
          </button>
          <button 
            className="btn-icon"
            onClick={handleClearChat}
            title="Clear Chat"
          >
            🗑️
          </button>
        </div>
      </div>

      {/* Document Uploader (collapsible) */}
      {showUploader && (
        <DocumentUploader
          onUpload={handleDocumentUpload}
          onClose={() => setShowUploader(false)}
          uploadedDocs={uploadedDocs}
          onRemoveDoc={handleRemoveDocument}
        />
      )}

      {/* Messages */}
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-welcome">
            <div className="welcome-icon">⚖️</div>
            <h3>Welcome to Propsten Legal Assistant</h3>
            <p>Upload legal documents and ask questions about:</p>
            <ul className="welcome-features">
              <li>✓ Property agreements and contracts</li>
              <li>✓ RERA certificates and compliance</li>
              <li>✓ Title deeds and ownership documents</li>
              <li>✓ Bank auction properties</li>
              <li>✓ Legal due diligence</li>
            </ul>
            <button 
              className="btn-secondary"
              onClick={() => setShowUploader(true)}
            >
              Upload Documents to Get Started
            </button>
          </div>
        ) : (
          <>
            <MessageList messages={messages} />
            {isTyping && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message Input */}
      <MessageInput 
        onSendMessage={handleSendMessage}
        disabled={isTyping}
      />
    </div>
  );
};

export default ChatInterface;
